import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useStore } from 'zustand';
import { useAuthStore } from '@/stores/authStore';
import {
  readWorkingView, workingViewAuthScope, workingViewRevision, workingViewStore,
  workingViewRecordKey, workingViewGroupKey,
  writeWorkingView, type WorkingViewAddress, type WorkingViewDefinition,
} from '@/stores/workingViewStore';

/** No effects restore visible defaults after paint. Consumers explicitly own URL fields. */
export function useWorkingView<T>(
  definition: WorkingViewDefinition<T>, instance: string, defaults: NoInfer<T>,
  options?: {
    fromUrl?: (value: T, search: string) => T;
    /** false: a PUSH/REPLACE without its own record starts from defaults
     * instead of resuming the latest visit. POP is unchanged. Default true. */
    resumeLatest?: boolean;
  },
) {
  const location = useLocation();
  const navigation = useNavigationType();
  const user = useAuthStore((s) => s.status === 'unauthenticated' ? null : s.user);
  const epoch = useStore(workingViewStore, (s) => s.epoch);
  const scope = workingViewAuthScope(user);
  const resumeLatest = navigation !== 'POP' && options?.resumeLatest !== false;
  const address: WorkingViewAddress = useMemo(() => ({
    scope: scope ?? '', epoch, view: definition.id, instance,
    entry: JSON.stringify([location.key, location.pathname, location.search]),
  }), [scope, epoch, definition.id, instance, location.key, location.pathname, location.search]);
  const addressId = JSON.stringify(address);
  // A scroll capture elsewhere must not rerender the trading controls.
  useStore(workingViewStore, (s) => s.records.get(workingViewRecordKey(address))
    ?? (resumeLatest ? s.records.get(s.latest.get(workingViewGroupKey(address)) ?? '') : undefined));
  const remembered = readWorkingView(definition, address, resumeLatest);
  const candidate = remembered ?? defaults;
  const value = definition.parse(options?.fromUrl?.(candidate, location.search) ?? candidate) ?? defaults;
  const currentRef = useRef<{ addressId: string; value: T } | null>({ addressId, value });
  useLayoutEffect(() => {
    currentRef.current = { addressId, value };
    return () => { currentRef.current = null; };
  });
  const [undoState, setUndoState] = useState<{ addressId: string; value: T; revision: number } | null>(null);

  // Register even an untouched history entry so Back restores that entry,
  // rather than a more recent visit's view. Runs before interaction/paint.
  useLayoutEffect(() => {
    if (scope && workingViewRevision(address) === undefined) writeWorkingView(definition, address, value);
    // Only a new scope/history entry is initialized. Actual changes write at the interaction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressId]);

  const update = useCallback((next: T | ((previous: T) => T)) => {
    // A late callback from a previous route must not rewrite its old snapshot
    // or make it the latest view after the reader has moved on.
    if (currentRef.current?.addressId !== addressId) return;
    const current = readWorkingView(definition, address, false) ?? currentRef.current.value;
    const resolved = typeof next === 'function' ? (next as (previous: T) => T)(current) : next;
    writeWorkingView(definition, address, resolved);
    setUndoState(null);
  }, [address, addressId, definition]);
  function reset() {
    // URL-owned fields stay URL-owned. A consumer that resets them must navigate
    // the relevant parameters explicitly; it must not mutate only this store.
    const resetValue = options?.fromUrl?.(defaults, location.search) ?? defaults;
    const revision = writeWorkingView(definition, address, resetValue);
    if (revision !== undefined) setUndoState({ addressId, value, revision });
  }
  const canUndo = undoState?.addressId === addressId
    && undoState.revision === workingViewRevision(address);
  function undo() {
    if (canUndo && undoState) writeWorkingView(definition, address, undoState.value);
    setUndoState(null);
  }
  return { value, update, reset, undo, canUndo, address };
}

export function useWorkingViewField<T, K extends keyof T>(
  view: { value: T; update: (next: T | ((previous: T) => T)) => void }, field: K,
): [T[K], (next: T[K] | ((previous: T[K]) => T[K])) => void] {
  const update = view.update;
  const set = useCallback((next: T[K] | ((previous: T[K]) => T[K])) => {
    update((previous) => ({ ...previous, [field]: typeof next === 'function'
      ? (next as (value: T[K]) => T[K])(previous[field]) : next }));
  }, [update, field]);
  return [view.value[field], set];
}
